import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useI18n } from "../i18n/I18nProvider";
import {
  ArrowRight, Package, Wrench, Box, Leaf, Truck, Users,
  Check, Star, Calendar, TrendingUp, Recycle, FileText, Globe,
  ImageIcon, BarChart3, Map, Minus,
} from "lucide-react";

const DEMO_EMAIL = "hello@resourcebuild.se";

export default function Landing() {
  const { t } = useI18n();

  return (
    <Layout>
      {/* ============ 1. HERO ============ */}
      <section className="max-w-[1440px] mx-auto px-6 pt-10" data-testid="landing-hero">
        <div className="grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <div className="col-span-12 lg:col-span-7 bg-[var(--bone)] p-8 md:p-14">
            <div className="label-tech mb-6">{t("lp.hero.pretitle")}</div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-7xl font-medium tracking-tighter text-[var(--ink)] leading-[0.95]">
              {t("lp.hero.h1a")}<br />
              <span className="italic text-[var(--brick)]">{t("lp.hero.h1b")}</span>
            </h1>
            <p className="mt-6 text-[var(--ink-700)] max-w-xl text-lg leading-relaxed">
              {t("lp.hero.sub")}
            </p>
            <div className="mt-4 label-tech text-[var(--ink-700)]" data-testid="hero-audience">
              {t("lp.hero.audience")}
            </div>
            <div className="mt-3 label-tech text-[var(--ink-500)]">{t("lp.hero.note")}</div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={`mailto:${DEMO_EMAIL}?subject=Demo%20request`} className="btn-primary" data-testid="hero-demo-btn">
                <Calendar size={14} /> {t("lp.hero.ctaDemo")}
              </a>
              <Link to="/marketplace" className="btn-ghost" data-testid="hero-try-btn">
                {t("lp.hero.ctaTry")} <ArrowRight size={14} />
              </Link>
              <a href="#features" className="text-sm font-mono-tech uppercase tracking-[0.1em] text-[var(--ink-500)] hover:text-[var(--ink)] underline decoration-dotted underline-offset-4" data-testid="hero-sample-btn">
                {t("lp.hero.ctaSample")} →
              </a>
            </div>

            <div className="mt-12 pt-6 border-t border-[var(--ink-200)] label-tech">
              {t("lp.hero.trust")}
            </div>
          </div>

          {/* Hero UI mockup placeholder */}
          <div className="col-span-12 lg:col-span-5 relative bg-[var(--ink)] min-h-[420px] p-6 md:p-8 flex flex-col" data-testid="hero-ui-mock">
            <div className="label-tech text-[var(--bone)]/60">{t("lp.hero.uiLabel")}</div>
            <div className="mt-3 flex-1 grid grid-cols-2 gap-2">
              <MockCard tone="bone" label="Materials" value="312" sub="listings" />
              <MockCard tone="brick" label="Machines" value="48" sub="booked" />
              <MockCard tone="moss" label="Vehicles" value="14" sub="active" />
              <MockCard tone="bone" label="CO₂e saved" value="2,140" sub="kg this month" />
              <div className="col-span-2 border p-4 mt-1" style={{ backgroundColor: "rgba(245,241,232,0.05)", borderColor: "rgba(245,241,232,0.2)" }}>
                <div className="text-[10px] font-mono-tech uppercase tracking-wider text-[var(--bone)]/60">Activity · last 7d</div>
                <div className="mt-3 flex items-end gap-1.5 h-14">
                  {[24, 38, 30, 52, 41, 47, 64].map((h, i) => (
                    <div key={i} className="flex-1 bg-[var(--brick)]" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 label-tech text-[var(--bone)]/60">{t("lp.hero.uiCaption")}</div>
          </div>
        </div>
      </section>

      {/* ============ 2. VALUE PROP ============ */}
      <section className="max-w-[1440px] mx-auto px-6 mt-24" data-testid="landing-value">
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter max-w-3xl">
          {t("lp.value.title")}
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          {["lp.value.v1", "lp.value.v2", "lp.value.v3", "lp.value.v4", "lp.value.v5"].map((k, i) => (
            <div key={k} className="flex items-start gap-3" data-testid={`value-${i}`}>
              <div className="w-6 h-6 bg-[var(--brick)] text-[var(--bone)] grid place-items-center shrink-0 mt-0.5">
                <Check size={12} strokeWidth={3} />
              </div>
              <div className="text-base text-[var(--ink-700)] leading-relaxed pt-0.5">{t(k)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 3. FLEET SPOTLIGHT ============ */}
      <section className="max-w-[1440px] mx-auto px-6 mt-24" data-testid="landing-fleet">
        <div className="grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <div className="col-span-12 lg:col-span-6 bg-[var(--bone)] p-8 md:p-12">
            <div className="label-tech">{t("lp.fleet.label")}</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tighter leading-[1.05]">
              {t("lp.fleet.title")}
            </h2>
            <p className="mt-5 text-[var(--ink-700)] leading-relaxed">{t("lp.fleet.sub")}</p>
            <ul className="mt-7 space-y-3">
              {["lp.fleet.b1", "lp.fleet.b2", "lp.fleet.b3", "lp.fleet.b4"].map((k, i) => (
                <li key={k} className="flex items-start gap-3 text-sm text-[var(--ink-700)] leading-relaxed" data-testid={`fleet-b-${i}`}>
                  <Check size={14} className="mt-1 text-[var(--brick)] shrink-0" strokeWidth={2.5} />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/vehicles" className="btn-primary" data-testid="fleet-cta">
                {t("lp.fleet.cta")} <ArrowRight size={14} />
              </Link>
              <Link to="/vehicles/rental-reports" className="btn-ghost" data-testid="fleet-rental-cta">
                {t("lp.fleet.rentalCta")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Fleet UI mockup — emission table */}
          <div className="col-span-12 lg:col-span-6 bg-[var(--surface-alt)] p-6 md:p-8" data-testid="fleet-ui-mock">
            <div className="flex items-center justify-between">
              <div className="label-tech text-[var(--ink-500)]">{t("lp.fleet.uiLabel")}</div>
              <Map size={16} className="text-[var(--ink-500)]" />
            </div>
            <div className="mt-4 bg-[var(--bone)] border border-[var(--ink-200)]">
              <div className="grid grid-cols-[1fr_60px_70px] gap-2 px-4 py-2 border-b border-[var(--ink-200)] text-[10px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)]">
                <div>Vehicle</div>
                <div className="text-right">km</div>
                <div className="text-right">kg CO₂e</div>
              </div>
              {[
                { reg: "ABC 123", name: "Volvo FH · Diesel", km: "4,250", co2: "1,284" },
                { reg: "DEF 456", name: "Scania R450 · HVO", km: "3,120", co2: "187" },
                { reg: "XYZ 789", name: "Tesla M3 · Electric", km: "1,820", co2: "14" },
                { reg: "GHI 012", name: "Volvo XC60 · Hybrid", km: "2,680", co2: "312" },
              ].map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_70px] gap-2 px-4 py-3 border-b border-[var(--ink-100)] last:border-b-0 items-center">
                  <div>
                    <div className="font-mono-tech text-xs">{r.reg}</div>
                    <div className="text-xs text-[var(--ink-500)] mt-0.5">{r.name}</div>
                  </div>
                  <div className="text-right text-xs font-mono-tech">{r.km}</div>
                  <div className="text-right text-xs font-mono-tech text-[var(--brick)]">{r.co2}</div>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_60px_70px] gap-2 px-4 py-3 bg-[var(--ink)] text-[var(--bone)]">
                <div className="text-[10px] font-mono-tech uppercase tracking-wider">Total CO₂e</div>
                <div className="text-right text-xs font-mono-tech">11,870</div>
                <div className="text-right text-xs font-mono-tech">1,797</div>
              </div>
            </div>
            <div className="mt-3 label-tech text-[var(--ink-500)]">{t("lp.fleet.uiCaption")}</div>
          </div>
        </div>
      </section>

      {/* ============ 4. CO2E SPOTLIGHT ============ */}
      <section className="max-w-[1440px] mx-auto px-6 mt-24" data-testid="landing-co2e">
        <div className="grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          {/* CO2e UI mockup — chart */}
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1 bg-[var(--surface-alt)] p-6 md:p-8" data-testid="co2e-ui-mock">
            <div className="flex items-center justify-between">
              <div className="label-tech text-[var(--ink-500)]">{t("lp.co2e.uiLabel")}</div>
              <BarChart3 size={16} className="text-[var(--moss)]" />
            </div>
            <div className="mt-4 bg-[var(--bone)] border border-[var(--ink-200)] p-5">
              <div className="flex items-baseline gap-3">
                <div className="font-display text-4xl tracking-tighter">2,140</div>
                <div className="label-tech text-[var(--ink-500)]">kg CO₂e total</div>
              </div>
              <div className="mt-5 flex items-end gap-1 h-24">
                {[18, 26, 22, 34, 42, 38, 56, 64, 72, 80, 88, 96].map((h, i) => (
                  <div key={i} className="flex-1 bg-[var(--moss)] hover:opacity-80 transition-opacity" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="mt-2 grid grid-cols-12 gap-1 text-[9px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)]">
                {["J","F","M","A","M","J","J","A","S","O","N","D"].map((m, i) => (
                  <div key={i} className="text-center">{m}</div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-[var(--ink-200)] grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)]">Rent vs buy</div>
                  <div className="mt-2 space-y-1.5">
                    <MockBar label="Buy new" value="94 kg" pct={94} tone="brick" />
                    <MockBar label="Rental" value="18 kg" pct={18} tone="moss" />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)]">Top machines</div>
                  <div className="mt-2 space-y-1.5">
                    <MockBar label="Minigrävare" value="820" pct={82} tone="ink" />
                    <MockBar label="Vibratorplatta" value="460" pct={46} tone="ink" />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 label-tech text-[var(--ink-500)]">{t("lp.co2e.uiCaption")}</div>
          </div>

          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2 bg-[var(--bone)] p-8 md:p-12">
            <div className="label-tech">{t("lp.co2e.label")}</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tighter leading-[1.05]">
              {t("lp.co2e.title")}
            </h2>
            <p className="mt-5 text-[var(--ink-700)] leading-relaxed">{t("lp.co2e.sub")}</p>

            {/* How calculation works */}
            <div className="mt-7 bg-[var(--surface-alt)] border border-[var(--ink-200)] p-5" data-testid="co2e-how">
              <div className="label-tech text-[var(--ink-500)]">{t("lp.co2e.howT")}</div>
              <ol className="mt-3 space-y-2.5">
                {["lp.co2e.how1", "lp.co2e.how2", "lp.co2e.how3"].map((k, i) => (
                  <li key={k} className="flex items-start gap-3 text-sm text-[var(--ink-700)] leading-relaxed">
                    <div className="w-5 h-5 grid place-items-center shrink-0 bg-[var(--moss)] text-[var(--bone)] font-mono-tech text-[10px] mt-0.5">
                      {i + 1}
                    </div>
                    <span>{t(k)}</span>
                  </li>
                ))}
              </ol>
            </div>

            <ul className="mt-7 grid grid-cols-2 gap-2.5">
              {["lp.co2e.b1", "lp.co2e.b2", "lp.co2e.b3", "lp.co2e.b4"].map((k, i) => (
                <li key={k} className="flex items-start gap-2 text-sm text-[var(--ink-700)]" data-testid={`co2e-b-${i}`}>
                  <Check size={12} className="mt-1 text-[var(--moss)] shrink-0" strokeWidth={2.5} />
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>

            <Link to="/dashboard/co2e" className="mt-8 btn-primary inline-flex" data-testid="co2e-cta">
              {t("lp.co2e.cta")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ 5. FEATURES ============ */}
      <section id="features" className="max-w-[1440px] mx-auto px-6 mt-24" data-testid="landing-features">
        <div className="label-tech">{t("lp.feat.label")}</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-3 max-w-3xl">{t("lp.feat.title")}</h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <FeatureCard icon={<Package size={20} />} title={t("lp.feat.materialT")} bullets={[t("lp.feat.material1"), t("lp.feat.material2"), t("lp.feat.material3")]} to="/marketplace" cta={t("lp.feat.materialCta")} testid="feat-material" mockLabel={t("lp.feat.uiLabel")} mockKind="material" />
          <FeatureCard icon={<Wrench size={20} />} title={t("lp.feat.machT")} bullets={[t("lp.feat.mach1"), t("lp.feat.mach2"), t("lp.feat.mach3")]} to="/machines" cta={t("lp.feat.machCta")} testid="feat-machines" mockLabel={t("lp.feat.uiLabel")} mockKind="machines" />
          <FeatureCard icon={<Truck size={20} />} title={t("lp.feat.vehT")} bullets={[t("lp.feat.veh1"), t("lp.feat.veh2"), t("lp.feat.veh3")]} to="/vehicles" cta={t("lp.feat.vehCta")} testid="feat-vehicles" mockLabel={t("lp.feat.uiLabel")} mockKind="vehicles" />
          <FeatureCard icon={<Box size={20} />} title={t("lp.feat.invT")} bullets={[t("lp.feat.inv1"), t("lp.feat.inv2"), t("lp.feat.inv3")]} to="/inventory" cta={t("lp.feat.invCta")} testid="feat-inventory" mockLabel={t("lp.feat.uiLabel")} mockKind="inventory" />
          <FeatureCard icon={<Users size={20} />} title={t("lp.feat.collabT")} bullets={[t("lp.feat.collab1"), t("lp.feat.collab2"), t("lp.feat.collab3")]} to="/dashboard" cta={t("lp.feat.collabCta")} testid="feat-collab" mockLabel={t("lp.feat.uiLabel")} mockKind="collab" />
          <FeatureCard icon={<Leaf size={20} />} title={t("lp.feat.co2T")} bullets={[t("lp.feat.co21"), t("lp.feat.co22"), t("lp.feat.co23")]} to="/dashboard/co2e" cta={t("lp.feat.co2Cta")} testid="feat-co2" accent="moss" mockLabel={t("lp.feat.uiLabel")} mockKind="co2" />
        </div>
      </section>

      {/* ============ 6. USP / WHY NOW ============ */}
      <section className="max-w-[1440px] mx-auto px-6 mt-24" data-testid="landing-usp">
        <div className="label-tech">{t("lp.uspLabel")}</div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-3 max-w-3xl">{t("lp.uspTitle")}</h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <UspCard icon={<TrendingUp size={18} />} title={t("lp.usp1T")} body={t("lp.usp1B")} testid="usp-1" />
          <UspCard icon={<Recycle size={18} />} title={t("lp.usp2T")} body={t("lp.usp2B")} testid="usp-2" />
          <UspCard icon={<FileText size={18} />} title={t("lp.usp3T")} body={t("lp.usp3B")} testid="usp-3" accent />
          <UspCard icon={<Globe size={18} />} title={t("lp.usp4T")} body={t("lp.usp4B")} testid="usp-4" />
        </div>
      </section>

      {/* ============ 7. SOCIAL PROOF PLACEHOLDER ============ */}
      <section className="mt-24 bg-[var(--surface-alt)]" data-testid="landing-proof">
        <div className="max-w-[1440px] mx-auto px-6 py-20">
          <div className="text-center label-tech">{t("lp.proofLabel")}</div>
          <h2 className="mt-3 text-center font-display text-3xl md:text-5xl tracking-tighter max-w-3xl mx-auto">
            {t("lp.proofTitle")}
          </h2>
          <p className="mt-5 text-center text-[var(--ink-500)] max-w-2xl mx-auto leading-relaxed">
            {t("lp.proofSub")}
          </p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="proof-logo-strip">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="aspect-[5/2] border border-dashed border-[var(--ink-300)] bg-[var(--bone)] grid place-items-center"
                data-testid={`proof-logo-${i}`}
              >
                <div className="flex flex-col items-center gap-2 text-[var(--ink-500)]">
                  <ImageIcon size={20} strokeWidth={1.5} />
                  <div className="label-tech opacity-70">{t("lp.proofLogoPlaceholder")}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 border border-dashed border-[var(--ink-300)] bg-[var(--bone)] p-8 md:p-10" data-testid="proof-case">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <div className="label-tech text-[var(--ink-500)]">[ Case study ]</div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">{t("lp.proofCaseT")}</h3>
                <p className="mt-3 text-[var(--ink-500)] leading-relaxed">{t("lp.proofCaseB")}</p>
              </div>
              <div className="flex md:justify-end">
                <a
                  href={`mailto:${DEMO_EMAIL}?subject=Pilot%20partner%20-%20Case%20study`}
                  className="btn-primary"
                  data-testid="proof-case-cta"
                >
                  {t("lp.proofCaseCta")} <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 8. PRICING ============ */}
      <section className="max-w-[1440px] mx-auto px-6 mt-24" data-testid="landing-pricing">
        <div className="text-center">
          <div className="label-tech">{t("lp.priceLabel")}</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-3">{t("lp.priceTitle")}</h2>
          <p className="mt-3 text-[var(--ink-500)]">{t("lp.priceSub")}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          <PriceCard
            title={t("lp.priceOpenT")}
            price={t("lp.priceOpenP")}
            priceHint={t("lp.priceOpenPHint")}
            desc={t("lp.priceOpenDesc")}
            bullets={[t("lp.priceOpen1"), t("lp.priceOpen2"), t("lp.priceOpen3"), t("lp.priceOpen4")]}
            ctaLabel={t("lp.priceOpenCta")}
            ctaTo="/marketplace"
            testid="price-open"
          />
          <PriceCard
            title={t("lp.priceHybridT")}
            price={t("lp.priceHybridP")}
            priceHint={t("lp.priceHybridPHint")}
            desc={t("lp.priceHybridDesc")}
            bullets={[t("lp.priceHybrid1"), t("lp.priceHybrid2"), t("lp.priceHybrid3"), t("lp.priceHybrid4"), t("lp.priceHybrid5")]}
            ctaLabel={t("lp.priceHybridCta")}
            ctaTo={`mailto:${DEMO_EMAIL}?subject=Hybrid%20demo`}
            recommended={t("lp.priceRec")}
            testid="price-hybrid"
          />
          <PriceCard
            title={t("lp.priceClosedT")}
            price={t("lp.priceClosedP")}
            priceHint={t("lp.priceClosedPHint")}
            desc={t("lp.priceClosedDesc")}
            bullets={[t("lp.priceClosed1"), t("lp.priceClosed2"), t("lp.priceClosed3"), t("lp.priceClosed4"), t("lp.priceClosed5")]}
            ctaLabel={t("lp.priceClosedCta")}
            ctaTo={`mailto:${DEMO_EMAIL}?subject=Enterprise%20inquiry`}
            testid="price-closed"
          />
        </div>

        {/* Comparison table */}
        <div className="mt-14 border border-[var(--ink-200)] bg-[var(--bone)]" data-testid="pricing-compare">
          <div className="px-5 py-4 border-b border-[var(--ink-200)] flex items-center justify-between">
            <div className="label-tech">{t("lp.cmp.title")}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--ink-200)] bg-[var(--surface-alt)]">
                  <th className="text-left px-5 py-3 font-mono-tech text-[10px] uppercase tracking-wider text-[var(--ink-500)]">{t("lp.cmp.colFeat")}</th>
                  <th className="px-3 py-3 font-mono-tech text-[10px] uppercase tracking-wider text-[var(--ink-500)] w-24 text-center">{t("lp.priceOpenT")}</th>
                  <th className="px-3 py-3 font-mono-tech text-[10px] uppercase tracking-wider text-[var(--brick)] w-24 text-center">{t("lp.priceHybridT")}</th>
                  <th className="px-3 py-3 font-mono-tech text-[10px] uppercase tracking-wider text-[var(--ink-500)] w-32 text-center">{t("lp.priceClosedT")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { k: "lp.cmp.r1", open: true, hybrid: true, closed: true },
                  { k: "lp.cmp.r2", open: false, hybrid: true, closed: true },
                  { k: "lp.cmp.r3", open: false, hybrid: true, closed: true },
                  { k: "lp.cmp.r4", open: false, hybrid: true, closed: true },
                  { k: "lp.cmp.r5", open: false, hybrid: true, closed: true },
                  { k: "lp.cmp.r6", open: false, hybrid: true, closed: true },
                  { k: "lp.cmp.r7", open: false, hybrid: false, closed: true },
                  { k: "lp.cmp.r8", open: false, hybrid: false, closed: true },
                ].map((row, i) => (
                  <tr key={row.k} className="border-b border-[var(--ink-100)] last:border-b-0" data-testid={`cmp-row-${i}`}>
                    <td className="px-5 py-3 text-[var(--ink-700)]">{t(row.k)}</td>
                    <td className="px-3 py-3 text-center"><CmpCell on={row.open} /></td>
                    <td className="px-3 py-3 text-center bg-[var(--brick)]/5"><CmpCell on={row.hybrid} accent /></td>
                    <td className="px-3 py-3 text-center"><CmpCell on={row.closed} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============ 9. CLOSING CTA ============ */}
      <section className="mt-24" data-testid="landing-end">
        <div className="bg-[var(--ink)] text-[var(--bone)]">
          <div className="max-w-[1440px] mx-auto px-6 py-20 text-center">
            <div className="label-tech opacity-60">{t("lp.endLabel")}</div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter mt-4 leading-[0.95]">
              {t("lp.endTitle")}
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-[var(--bone)]/80 text-lg leading-relaxed">
              {t("lp.endSub")}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <a href={`mailto:${DEMO_EMAIL}?subject=Demo%20request`} className="inline-flex items-center gap-2 px-6 py-4 bg-[var(--brick)] text-[var(--bone)] font-mono-tech uppercase tracking-[0.1em] text-xs hover:opacity-90 transition-opacity" data-testid="end-demo-btn">
                <Calendar size={14} /> {t("lp.hero.ctaDemo")}
              </a>
              <a href="#features" className="inline-flex items-center gap-2 px-6 py-4 border border-[var(--bone)]/40 text-[var(--bone)] font-mono-tech uppercase tracking-[0.1em] text-xs hover:bg-[var(--bone)]/10 transition-colors" data-testid="end-explore-btn">
                {t("lp.hero.ctaSample")} <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-8 label-tech opacity-50">{t("lp.endTrust")}</div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

// ============ Sub-components ============

function MockCard({ tone = "bone", label, value, sub }) {
  const styles = {
    bone: "bg-[var(--bone)]/15 border-[var(--bone)]/30 text-[var(--bone)]",
    brick: "bg-[var(--brick)] border-[var(--brick)] text-[var(--bone)]",
    moss: "bg-[var(--moss)] border-[var(--moss)] text-[var(--bone)]",
  };
  return (
    <div className={`border p-3 ${styles[tone]}`} style={tone === "bone" ? { backgroundColor: "rgba(245,241,232,0.08)", borderColor: "rgba(245,241,232,0.25)" } : undefined}>
      <div className="text-[9px] font-mono-tech uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display text-2xl tracking-tighter mt-1">{value}</div>
      <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>
    </div>
  );
}

function MockBar({ label, value, pct, tone = "ink" }) {
  const toneClass = tone === "brick" ? "bg-[var(--brick)]" : tone === "moss" ? "bg-[var(--moss)]" : "bg-[var(--ink)]";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-mono-tech">
        <span className="text-[var(--ink-700)]">{label}</span>
        <span className="text-[var(--ink-500)]">{value}</span>
      </div>
      <div className="mt-1 h-1.5 bg-[var(--ink-100)]">
        <div className={`h-full ${toneClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function FeatureMock({ kind }) {
  // Tiny UI preview strips per feature kind
  if (kind === "material") {
    return (
      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex-1 aspect-square bg-[var(--surface-alt)] border border-[var(--ink-200)] grid place-items-center">
            <Package size={14} className="text-[var(--ink-300)]" />
          </div>
        ))}
      </div>
    );
  }
  if (kind === "machines") {
    return (
      <div className="grid grid-cols-7 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
        {[0,1,1,2,1,0,0].map((s, i) => (
          <div key={i} className={`aspect-square ${s===2?"bg-[var(--brick)]":s===1?"bg-[var(--brick)]/40":"bg-[var(--bone)]"}`} />
        ))}
      </div>
    );
  }
  if (kind === "vehicles") {
    return (
      <div className="bg-[var(--surface-alt)] border border-[var(--ink-200)] p-2 space-y-1">
        {["ABC 123","DEF 456","XYZ 789"].map((r) => (
          <div key={r} className="flex items-center justify-between text-[10px] font-mono-tech">
            <span>{r}</span>
            <span className="text-[var(--brick)]">●●○</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "inventory") {
    return (
      <div className="grid grid-cols-4 gap-1">
        {["B1","B2","S1","S2","C1","C2","K1","K2"].map((c) => (
          <div key={c} className="aspect-square border border-[var(--ink-200)] bg-[var(--surface-alt)] grid place-items-center font-mono-tech text-[9px] text-[var(--ink-500)]">{c}</div>
        ))}
      </div>
    );
  }
  if (kind === "collab") {
    return (
      <div className="relative h-16 bg-[var(--surface-alt)] border border-[var(--ink-200)]">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[var(--brick)] grid place-items-center text-[10px] font-mono-tech text-[var(--bone)]">YOU</div>
        {[20, 45, 70].map((x, i) => (
          <div key={i} className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[var(--ink)] border-2 border-[var(--bone)] grid place-items-center" style={{ left: `${x}%` }}>
            <Users size={10} className="text-[var(--bone)]" />
          </div>
        ))}
      </div>
    );
  }
  if (kind === "co2") {
    return (
      <div className="bg-[var(--surface-alt)] border border-[var(--ink-200)] p-2 flex items-end gap-1 h-16">
        {[20, 35, 28, 48, 56, 42, 70].map((h, i) => (
          <div key={i} className="flex-1 bg-[var(--moss)]" style={{ height: `${h}%` }} />
        ))}
      </div>
    );
  }
  return null;
}

function FeatureCard({ icon, title, bullets, to, cta, accent, testid, mockLabel, mockKind }) {
  const iconColor = accent === "moss" ? "text-[var(--moss)]" : "text-[var(--brick)]";
  return (
    <div className="bg-[var(--bone)] p-6 lg:p-8 flex flex-col group hover:bg-[var(--surface-alt)] transition-colors" data-testid={testid}>
      {/* UI preview placeholder */}
      <div className="border border-dashed border-[var(--ink-200)] bg-[var(--surface-alt)]/40 p-2.5" data-testid={`${testid}-mock`}>
        <div className="text-[9px] font-mono-tech uppercase tracking-wider text-[var(--ink-500)] flex items-center gap-1.5">
          <ImageIcon size={10} strokeWidth={1.5} />
          {mockLabel}
        </div>
        <div className="mt-2">
          <FeatureMock kind={mockKind} />
        </div>
      </div>

      <div className={`${iconColor} mt-5`}>{icon}</div>
      <h3 className="font-display text-xl md:text-2xl tracking-tight mt-2">{title}</h3>
      <ul className="mt-4 space-y-2 flex-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink-700)] leading-relaxed">
            <Check size={12} className="mt-1 text-[var(--ink-500)] shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Link to={to} className="mt-6 label-tech inline-flex items-center gap-1 text-[var(--ink)] hover:text-[var(--brick)] transition-colors">
        {cta} <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function UspCard({ icon, title, body, accent, testid }) {
  return (
    <div className={`border ${accent ? "border-[var(--brick)] bg-[var(--brick)]/5" : "border-[var(--ink-200)] bg-[var(--bone)]"} p-6`} data-testid={testid}>
      <div className={`w-9 h-9 grid place-items-center mb-4 ${accent ? "bg-[var(--brick)] text-[var(--bone)]" : "bg-[var(--ink)] text-[var(--bone)]"}`}>
        {icon}
      </div>
      <div className="font-display text-lg tracking-tight">{title}</div>
      <p className="mt-3 text-sm text-[var(--ink-700)] leading-relaxed">{body}</p>
    </div>
  );
}

function PriceCard({ title, price, priceHint, desc, bullets, ctaLabel, ctaTo, recommended, testid }) {
  const isMailto = ctaTo?.startsWith("mailto:");
  const CtaTag = isMailto ? "a" : Link;
  const ctaProps = isMailto ? { href: ctaTo } : { to: ctaTo };
  return (
    <div
      className={`bg-[var(--bone)] border-2 p-6 lg:p-8 flex flex-col relative ${recommended ? "border-[var(--brick)] lg:scale-[1.03]" : "border-[var(--ink-200)]"}`}
      data-testid={testid}
    >
      {recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--brick)] text-[var(--bone)] px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider inline-flex items-center gap-1">
          <Star size={10} /> {recommended}
        </div>
      )}
      <div className="font-display text-xl tracking-tight">{title}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-display text-3xl md:text-4xl tracking-tighter">{price}</div>
        {priceHint && (
          <div className="label-tech text-[var(--ink-500)]" data-testid={`${testid}-hint`}>{priceHint}</div>
        )}
      </div>
      <div className="mt-2 text-sm text-[var(--ink-500)]">{desc}</div>
      <ul className="mt-6 space-y-2 flex-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[var(--ink-700)]">
            <Check size={12} className="mt-1 text-[var(--brick)] shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <CtaTag
        {...ctaProps}
        className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 font-mono-tech uppercase tracking-[0.1em] text-xs transition-colors ${
          recommended
            ? "bg-[var(--brick)] text-[var(--bone)] hover:opacity-90"
            : "border border-[var(--ink-200)] text-[var(--ink)] hover:bg-[var(--surface-alt)]"
        }`}
        data-testid={`${testid}-cta`}
      >
        {ctaLabel} <ArrowRight size={14} />
      </CtaTag>
    </div>
  );
}

function CmpCell({ on, accent }) {
  if (on) {
    return (
      <div className={`inline-grid place-items-center w-5 h-5 ${accent ? "bg-[var(--brick)] text-[var(--bone)]" : "bg-[var(--ink)] text-[var(--bone)]"}`}>
        <Check size={11} strokeWidth={3} />
      </div>
    );
  }
  return <Minus size={14} className="text-[var(--ink-300)] inline" />;
}
